import { BULLETIN } from '../config/church'
import type { Screen } from '../core/types'
import { homeBar, pageHeader } from './chrome'

/**
 * Weekly bulletin viewer.
 *
 * The bulletin is published as a single LANDSCAPE JPEG (2100x1275) with no text
 * layer, laid out in three dense columns for print. Dropped whole onto a
 * portrait panel it renders 1080x655 — the body text lands around 4px tall and
 * is flatly unreadable, and with no text layer we cannot reflow it.
 *
 * So the viewer never shows it whole as the primary view. It offers an overview
 * plus one button per printed column that zooms that column to a readable size
 * with vertical panning. This works with the artwork the church already
 * publishes every week — no new production step — but it is a workaround, not
 * a fix. See README "주보 표시 문제".
 */

const VIEW_W = 1080
const VIEW_H = 1180
const COLUMNS = 3

export function createBulletinScreen(): Screen {
  let detach: (() => void) | undefined

  return {
    mount(root, ctx) {
      root.classList.add('screen--page', 'screen--bulletin')
      root.appendChild(pageHeader('이번주 주보', BULLETIN.label))

      const viewport = document.createElement('div')
      viewport.className = 'bulletin__viewport'

      const image = document.createElement('img')
      image.className = 'bulletin__image'
      image.src = BULLETIN.image
      image.alt = BULLETIN.label
      image.decoding = 'async'

      const failure = document.createElement('p')
      failure.className = 'bulletin__failure'
      failure.textContent = '주보를 불러오지 못했습니다. 네트워크를 확인해 주세요.'
      failure.hidden = true

      // A wall panel must never show a broken-image icon.
      image.addEventListener('error', () => {
        image.hidden = true
        failure.hidden = false
      })

      viewport.append(image, failure)
      root.appendChild(viewport)

      const colWidth = BULLETIN.width / COLUMNS
      const fitScale = VIEW_W / BULLETIN.width
      const zoomScale = (VIEW_W * 0.94) / colWidth

      let mode: 'fit' | number = 'fit'
      let offsetY = 0

      const render = (): void => {
        let scale: number
        let x: number
        if (mode === 'fit') {
          scale = fitScale
          x = 0
          offsetY = (VIEW_H - BULLETIN.height * fitScale) / 2
        } else {
          scale = zoomScale
          x = -(mode * colWidth * scale) + (VIEW_W - colWidth * scale) / 2
          const min = Math.min(0, VIEW_H - BULLETIN.height * scale)
          offsetY = Math.max(min, Math.min(0, offsetY))
        }
        image.style.width = `${BULLETIN.width * scale}px`
        image.style.transform = `translate(${x}px, ${offsetY}px)`
      }

      // Vertical drag, only meaningful when a column is zoomed in.
      let dragging = false
      let lastY = 0
      const onDown = (event: PointerEvent): void => {
        if (mode === 'fit') return
        dragging = true
        lastY = event.clientY
        viewport.setPointerCapture(event.pointerId)
      }
      const onMove = (event: PointerEvent): void => {
        if (!dragging) return
        // clientY is in viewport pixels; divide out the stage scale so a finger
        // moving 1 real pixel moves the image 1 design pixel.
        const stageScale =
          Number(document.documentElement.style.getPropertyValue('--stage-scale')) || 1
        offsetY += (event.clientY - lastY) / stageScale
        lastY = event.clientY
        render()
      }
      const onUp = (): void => {
        dragging = false
      }
      viewport.addEventListener('pointerdown', onDown)
      viewport.addEventListener('pointermove', onMove)
      viewport.addEventListener('pointerup', onUp)
      viewport.addEventListener('pointercancel', onUp)
      detach = () => {
        viewport.removeEventListener('pointerdown', onDown)
        viewport.removeEventListener('pointermove', onMove)
        viewport.removeEventListener('pointerup', onUp)
        viewport.removeEventListener('pointercancel', onUp)
      }

      const controls = document.createElement('div')
      controls.className = 'bulletin__controls'
      const buttons: HTMLButtonElement[] = []

      const select = (next: 'fit' | number): void => {
        mode = next
        if (next !== 'fit') offsetY = 0
        render()
        buttons.forEach((button, index) => {
          const active = next === 'fit' ? index === 0 : index === next + 1
          button.classList.toggle('is-active', active)
        })
      }

      const labels: Array<{ text: string; value: 'fit' | number }> = [
        { text: '전체', value: 'fit' },
        { text: '1단', value: 0 },
        { text: '2단', value: 1 },
        { text: '3단', value: 2 },
      ]
      for (const entry of labels) {
        const button = document.createElement('button')
        button.type = 'button'
        button.className = 'bulletin__tab'
        button.textContent = entry.text
        button.addEventListener('click', () => select(entry.value))
        controls.appendChild(button)
        buttons.push(button)
      }
      root.appendChild(controls)

      const hint = document.createElement('p')
      hint.className = 'bulletin__hint'
      hint.textContent = '단을 선택한 뒤 위아래로 밀어서 읽으세요'
      root.appendChild(hint)

      // Default to the first column, not the overview: at fit scale the body
      // text is ~4px tall, so "전체" orients you but cannot be read. Opening on
      // something legible is the whole point.
      select(0)
      root.appendChild(homeBar(ctx.go))
    },

    unmount() {
      detach?.()
    },
  }
}
